import React from "react";
import siteConfig from "../config/siteConfig";
import PolicyPage from "./PolicyPage";

const email = siteConfig.contact.email;
const brand = siteConfig.brandName;
const legalName = siteConfig.company.legalName;
const address = siteConfig.contact.address;

export const additionalPolicyRoutes = [
  { key: "payments", path: "/payments", label: "Payment policy" },
  { key: "cancellations", path: "/cancellations", label: "Cancellation policy" },
  { key: "warranty", path: "/warranty", label: "Warranty and faulty goods" },
  { key: "product-information", path: "/product-information", label: "Product information and care" },
  { key: "promotions", path: "/promotions", label: "Promotions and discount codes" },
  { key: "acceptable-use", path: "/acceptable-use", label: "Acceptable use policy" },
  { key: "intellectual-property", path: "/intellectual-property", label: "Intellectual property policy" },
  { key: "accessibility", path: "/accessibility", label: "Accessibility statement" },
  { key: "complaints", path: "/complaints", label: "Complaints policy" },
  { key: "security", path: "/security", label: "Security and fraud prevention" },
  { key: "product-safety", path: "/product-safety", label: "Product safety and recalls" },
  { key: "legal-notice", path: "/legal-notice", label: "Legal notice" },
];

const policyDocuments = {
  payments: {
    title: "Payment policy",
    intro: `How payments, authorisations and refunds are handled at ${brand}.`,
    sections: [
      {
        id: "accepted-methods",
        title: "Accepted payment methods",
        paragraphs: [
          `Checkout currently supports eligible Visa, Mastercard, American Express and UnionPay cards through our payment partner. The methods actually available to you are those shown on the secure payment page. All orders are charged in ${siteConfig.currency}.`,
          "A card issuer may apply its own exchange rate, cross-border charge or other fee. Those charges are set by the issuer and are not collected by us.",
        ],
      },
      {
        id: "authorisation",
        title: "Authorisation and capture",
        paragraphs: [
          "When you submit payment, you authorise the total displayed at checkout. The payment provider or card issuer may authenticate the transaction and place a temporary hold. An order is not accepted until the stage described in our terms and conditions.",
          "If an order is declined or cancelled, we do not intentionally collect the cancelled amount. A pending bank authorisation may remain visible until the issuer releases it; the release time is controlled by the issuer.",
        ],
      },
      {
        id: "security",
        title: "Payment security",
        paragraphs: [
          "Card numbers and card security codes are entered on the secure page operated by our payment partner and are not intended to be stored by this storefront. Payment providers and issuers may use authentication, device and transaction information to prevent fraud.",
          `We will never ask you to send a full card number, card security code or banking password by email. Report a suspicious payment message to ${email}.`,
        ],
      },
      {
        id: "failed-payment",
        title: "Failed or duplicate payments",
        paragraphs: [
          "A payment may fail because of issuer controls, incorrect details, authentication failure, an expired card, insufficient funds or a network interruption. Check with the issuer before repeatedly submitting payment.",
          `If two completed charges appear for one order, email ${email} with the order number, charge dates and amounts. Do not send full card details. We will compare our transaction records and arrange correction where a duplicate is confirmed.`,
        ],
      },
      {
        id: "verification",
        title: "Verification and fraud review",
        paragraphs: [
          "We may pause an order and request proportionate information to confirm the purchaser, delivery details or authority to use a payment method. We may cancel a transaction where verification is not completed or fraud is reasonably suspected. Any personal data used in that review is handled under our privacy policy.",
        ],
      },
      {
        id: "refunds",
        title: "Refunds",
        paragraphs: [
          "Approved refunds are sent to the original payment method unless applicable law requires another approach. We normally initiate the refund within 5–10 working days after an approved return is inspected or a cancellation is confirmed. The issuer may need additional time to post the credit.",
          "Delivery charges, return costs and promotional adjustments are refunded only as described in the returns policy or as required by law. A refund cannot normally be redirected to a different card or person.",
        ],
      },
      {
        id: "chargebacks",
        title: "Payment disputes",
        paragraphs: [
          `Please contact ${email} first if you do not recognise a charge or believe an amount is incorrect. We will review the order and provide a clear response. This does not prevent you from exercising any dispute right available through your issuer or under law.`,
        ],
      },
    ],
    relatedLinks: [
      { label: "Terms and conditions", to: "/terms" },
      { label: "Returns and refunds", to: "/returns" },
      { label: "Security and fraud", to: "/security" },
      { label: "All policies", to: "/policies" },
    ],
  },
  cancellations: {
    title: "Cancellation policy",
    intro: "How to request an order change or cancellation before and after dispatch.",
    sections: [
      {
        id: "request",
        title: "Requesting a cancellation",
        paragraphs: [
          `Email ${email} as soon as possible with your order number and the word “Cancellation” in the subject line. A request is not confirmed until we reply. We will make a reasonable effort to stop the order, but fulfilment may begin shortly after payment.`,
        ],
      },
      {
        id: "before-dispatch",
        title: "Before dispatch",
        paragraphs: [
          "If the order has not entered a stage that cannot reasonably be stopped, we will cancel the requested items and refund the corresponding amount to the original payment method. A bank may take additional time to release an authorisation or display the refund.",
        ],
      },
      {
        id: "changes",
        title: "Address and order changes",
        paragraphs: [
          "We cannot guarantee changes to an item, size, colour, delivery method or address after checkout. For security, a material delivery-address change may require cancellation and a new order. We will not redirect an order to an unrelated recipient based only on an unverified email request.",
        ],
      },
      {
        id: "after-dispatch",
        title: "After dispatch",
        paragraphs: [
          `Once a parcel has been dispatched or can no longer be stopped, the order cannot be cancelled through this process. You may request an eligible return after delivery within the ${siteConfig.shipping.returnsWindowDays}-day return window. Do not refuse a parcel unless customer care instructs you to do so, because refusal can delay tracking and a refund.`,
        ],
      },
      {
        id: "seller-cancellation",
        title: "Cancellation by us",
        paragraphs: [
          "We may cancel before acceptance because of unavailable stock, failed payment, suspected fraud, an obvious pricing or description error, an undeliverable destination, a purchase-limit breach or a legal restriction. We will explain the reason where lawful and refund any captured amount.",
        ],
      },
      {
        id: "rights",
        title: "Mandatory cancellation rights",
        paragraphs: [
          "Some locations give consumers a statutory cooling-off right for distance purchases. Any such non-excludable right applies in addition to this policy. Product exceptions, notice periods, return obligations and deductions are determined by the law that applies to your purchase.",
        ],
      },
    ],
    relatedLinks: [
      { label: "Returns and refunds", to: "/returns" },
      { label: "Shipping", to: "/shipping" },
      { label: "Contact", to: "/contact" },
      { label: "All policies", to: "/policies" },
    ],
  },
  warranty: {
    title: "Warranty and faulty goods policy",
    intro: "Our process for manufacturing faults, damage and non-conforming products.",
    sections: [
      {
        id: "statutory-rights",
        title: "Your legal rights",
        paragraphs: [
          "Products must match their description and meet any non-excludable standards that apply to the sale. This policy is in addition to, and does not replace, rights or remedies for goods that are faulty, unsafe, misdescribed or not of legally required quality.",
        ],
      },
      {
        id: "coverage",
        title: "What may be a fault",
        paragraphs: [
          "A fault may include a defect in materials or workmanship that was present on delivery or becomes apparent through normal use. Examples can include a failed seam, defective fastening, incorrect construction or a material that materially differs from the product description.",
          "Natural variations disclosed in the product information, normal knitwear pilling, gradual colour change, ordinary wear, accidental damage, misuse, alteration, snagging, moth damage or failure to follow the care label are not normally manufacturing faults.",
        ],
      },
      {
        id: "report",
        title: "How to report a problem",
        paragraphs: [
          `Email ${email} with your order number, a description of the issue, when it appeared and clear photographs of the whole product, the affected area and the care label. Please stop using or washing an item if continued use could worsen the issue.`,
        ],
      },
      {
        id: "assessment",
        title: "Assessment",
        paragraphs: [
          "We may request additional photographs or arrange a return for inspection. The assessment considers the product, age, expected use, care instructions and nature of the issue. Do not send an item until return instructions are provided. Reasonable return costs for a confirmed fault will be covered.",
        ],
      },
      {
        id: "remedies",
        title: "Available remedies",
        paragraphs: [
          "Where a fault is confirmed, the appropriate remedy may be repair, replacement, price reduction or refund, depending on the circumstances, stock availability and the rights that apply. We will not substitute a different product without your agreement.",
        ],
      },
      {
        id: "third-party-repairs",
        title: "Unauthorised repair or alteration",
        paragraphs: [
          "Please contact us before arranging a repair. We cannot reimburse a third-party service that was not approved in advance, except where applicable law provides otherwise. An unrelated alteration or repair may make it harder to identify the original cause but does not automatically remove a legal right.",
        ],
      },
    ],
    relatedLinks: [
      { label: "Product information and care", to: "/product-information" },
      { label: "Returns and refunds", to: "/returns" },
      { label: "Product safety and recalls", to: "/product-safety" },
      { label: "All policies", to: "/policies" },
    ],
  },
  "product-information": {
    title: "Product information and care policy",
    intro: "How to read product details, choose a size and care for knitwear.",
    sections: [
      {
        id: "descriptions",
        title: "Product descriptions",
        paragraphs: [
          "We aim to present material, construction, colour, measurements and care information clearly. Product-page information should be read together with any labels supplied with the item. If information conflicts, contact us before use or washing.",
        ],
      },
      {
        id: "colour-variation",
        title: "Colour and natural variation",
        paragraphs: [
          "Screens, lighting and photography can change how a colour appears. Natural fibres and small production batches may show minor differences in shade, texture, weight or dimensions. Pattern placement may also vary. We do not treat a disclosed, reasonable natural variation as a defect.",
        ],
      },
      {
        id: "sizing",
        title: "Sizing and measurements",
        paragraphs: [
          "Use the measurements on the relevant product page rather than relying only on a general size label. Garment measurements are approximate and can vary slightly because of material and construction. Knitwear may relax during wear and recover after appropriate washing and reshaping.",
        ],
      },
      {
        id: "care",
        title: "Care instructions",
        paragraphs: [
          "Follow the sewn-in care label. Unless the label says otherwise, avoid unnecessary washing, close fastenings, use a suitable gentle detergent, reshape while damp and dry flat away from direct heat. Do not tumble dry or use bleach unless expressly permitted. Store clean knitwear folded rather than hanging it for long periods.",
        ],
      },
      {
        id: "pilling",
        title: "Pilling and wear",
        paragraphs: [
          "Pilling is the formation of small fibre balls where fabric rubs. It can occur naturally in soft wool and other fibres and is not by itself evidence of a defect. Reduce friction, rotate wear and remove pills carefully with a tool designed for the material.",
        ],
      },
      {
        id: "allergies-safety",
        title: "Fibres, allergies and safety",
        paragraphs: [
          "Review the fibre composition before purchase if you have a known sensitivity. Stop using a product if it causes irritation. Keep packaging and detachable small parts away from children. Product information is not medical advice.",
        ],
      },
      {
        id: "questions",
        title: "Questions before purchase",
        paragraphs: [
          `Email ${email} if you need clarification about fibre content, measurements, care or a product detail before ordering. We prefer to answer a question rather than have you rely on an assumption that is not shown on the product page.`,
        ],
      },
    ],
    relatedLinks: [
      { label: "Warranty and faulty goods", to: "/warranty" },
      { label: "Returns and refunds", to: "/returns" },
      { label: "Product safety and recalls", to: "/product-safety" },
      { label: "All policies", to: "/policies" },
    ],
  },
  promotions: {
    title: "Promotions and discount codes policy",
    intro: "Rules for promotional offers, codes and price adjustments.",
    sections: [
      {
        id: "offer-terms",
        title: "Offer terms",
        paragraphs: [
          "Each promotion may state its own start and end time, eligible products, territories, customer eligibility, minimum spend and exclusions. Those specific terms apply together with this policy. Times refer to Hong Kong time unless an offer states otherwise.",
        ],
      },
      {
        id: "codes",
        title: "Using a code",
        paragraphs: [
          "Enter a valid code before payment and check that the discount appears in the order summary. Unless stated otherwise, one code may be used per order, codes cannot be combined, and a code has no cash value. A code cannot normally be added after checkout.",
        ],
      },
      {
        id: "exclusions",
        title: "Common exclusions",
        paragraphs: [
          "Delivery charges, duties, taxes, previously purchased items and products identified in the offer may be excluded. A spending threshold is calculated after other eligible discounts and before delivery unless stated otherwise.",
        ],
      },
      {
        id: "availability",
        title: "Availability and errors",
        paragraphs: [
          "Promotions do not reserve stock. We may correct an obvious technical or pricing error and may suspend an offer where necessary to prevent wider harm. If an error affects a placed order, we will explain the options before acceptance or cancel and refund the affected amount.",
        ],
      },
      {
        id: "misuse",
        title: "Fair use",
        paragraphs: [
          "Codes intended for a limited recipient or single use must not be sold, automated, manipulated or obtained through false identities. We may withdraw an improperly obtained discount, apply purchase limits or cancel an abusive transaction, while respecting applicable consumer rights.",
        ],
      },
      {
        id: "returns",
        title: "Returns and partial refunds",
        paragraphs: [
          "A refund reflects the amount actually paid for the returned item after the promotion. If a partial return means the original order no longer meets a stated offer condition, the refund may be recalculated where the offer terms clearly provided for that result and applicable law permits it.",
        ],
      },
      {
        id: "price-adjustments",
        title: "Price adjustments",
        paragraphs: [
          "We do not normally provide retrospective discounts or price matching after an order is placed. This does not affect correction of an actual pricing error or a remedy required by law.",
        ],
      },
    ],
    relatedLinks: [
      { label: "Terms and conditions", to: "/terms" },
      { label: "Returns and refunds", to: "/returns" },
      { label: "Payment policy", to: "/payments" },
      { label: "All policies", to: "/policies" },
    ],
  },
  "acceptable-use": {
    title: "Acceptable use policy",
    intro: `Rules that protect visitors and the ${brand} website from misuse.`,
    sections: [
      {
        id: "permitted-use",
        title: "Permitted use",
        paragraphs: [
          "You may browse, search, save products, place genuine orders and retain copies of your own order records for lawful personal use. Any commercial or automated use requires prior written permission unless applicable law expressly permits it.",
        ],
      },
      {
        id: "prohibited-conduct",
        title: "Prohibited conduct",
        items: [
          "Attempting to gain unauthorised access, probe security, bypass rate limits or interfere with the website or another user's device.",
          "Introducing malware, harmful code, denial-of-service traffic, fraudulent payment data or content intended to deceive.",
          "Using bots, scrapers or automated purchasing tools in a way that burdens the service, defeats purchase limits or extracts substantial content.",
          "Impersonating a person, misrepresenting affiliation, creating false orders or using another person's personal or payment data without authority.",
          "Collecting personal data from the site, sending unlawful marketing or using the service to harass, discriminate or violate another person's rights.",
          "Copying content, removing rights notices or creating a confusing imitation of the store in breach of intellectual-property law.",
        ],
      },
      {
        id: "links",
        title: "Linking to the site",
        paragraphs: [
          `You may link fairly to the public homepage or a product page if the link is lawful and does not imply sponsorship, partnership or endorsement. You must not frame the site, obscure its source, use the ${brand} name in a misleading domain or redirect visitors through deceptive content.`,
        ],
      },
      {
        id: "enforcement",
        title: "Enforcement",
        paragraphs: [
          "We may investigate suspected misuse, preserve relevant evidence, limit access, cancel fraudulent orders and report unlawful conduct to service providers or authorities. Measures will be proportionate to the risk and handled under the privacy policy and applicable law.",
        ],
      },
      {
        id: "reporting",
        title: "Reporting misuse",
        paragraphs: [
          `Report phishing, a fake store, security abuse or other misuse to ${email}. Include the relevant URL, date and a description, but do not send passwords or full payment-card details.`,
        ],
      },
    ],
    relatedLinks: [
      { label: "Intellectual property", to: "/intellectual-property" },
      { label: "Security and fraud", to: "/security" },
      { label: "Terms and conditions", to: "/terms" },
      { label: "All policies", to: "/policies" },
    ],
  },
  "intellectual-property": {
    title: "Intellectual property policy",
    intro: "Ownership, permitted use and a process for reporting possible infringement.",
    sections: [
      {
        id: "ownership",
        title: "Our content",
        paragraphs: [
          `The ${brand} name, original store text, product descriptions, graphics, photographs, logos, layout and other original material are owned by ${legalName} or used under an appropriate licence. Applicable copyright, trade mark, design and unfair-competition laws protect that material.`,
          "Third-party software, typefaces, payment marks and other identified materials remain the property of their respective owners and are used subject to their licences or lawful exceptions.",
        ],
      },
      {
        id: "permission",
        title: "Limited personal use",
        paragraphs: [
          "You may view the website and retain a copy of your own order information for personal, non-commercial use. No other licence is granted. In particular, you may not reproduce a substantial part of the catalogue, resell images or copy, adapt or publish protected material without written permission.",
        ],
      },
      {
        id: "brand-use",
        title: "Names and endorsement",
        paragraphs: [
          `You must not register or use a name, domain, account, advertisement or store presentation that is confusingly similar to ${brand}, or state or imply that a third party is connected with or endorsed by us when that is not true. Likewise, ${brand} does not claim affiliation with or endorsement by another brand unless expressly stated.`,
        ],
      },
      {
        id: "customer-material",
        title: "Material you send",
        paragraphs: [
          "You must own or have permission to send photographs, text or other material to us. You retain your rights. You give us a limited, non-exclusive permission to use that material only to answer your request, investigate a product or rights issue, comply with law or provide the service you requested. Public marketing use requires separate permission.",
        ],
      },
      {
        id: "report",
        title: "Report possible infringement",
        paragraphs: [
          `A rights owner or authorised representative may email ${email} with: their name and contact details; the work, mark or right concerned; the exact page or material at issue; the basis of ownership; why the use is not authorised; and a statement that the report is accurate.`,
          "We will review a sufficiently detailed notice, may temporarily restrict material while checking it, and may request verification or a response from the relevant supplier. Knowingly false claims may cause harm, so submit a report only in good faith.",
        ],
      },
      {
        id: "repeat",
        title: "Repeat or serious infringement",
        paragraphs: [
          "Where content or a supplier is found to repeatedly or seriously infringe rights, we may remove the material, end the relevant relationship and preserve records needed to resolve the claim, subject to applicable law.",
        ],
      },
    ],
    relatedLinks: [
      { label: "Acceptable use", to: "/acceptable-use" },
      { label: "Legal notice", to: "/legal-notice" },
      { label: "Contact", to: "/contact" },
      { label: "All policies", to: "/policies" },
    ],
  },
  accessibility: {
    title: "Accessibility statement",
    intro: `Our approach to making ${brand} usable by as many people as possible.`,
    sections: [
      {
        id: "commitment",
        title: "Our commitment",
        paragraphs: [
          "We aim to provide a shopping experience that people can perceive, understand, navigate and operate using a range of devices and assistive technologies. Accessibility is an ongoing part of design, content and maintenance rather than a one-time claim.",
        ],
      },
      {
        id: "measures",
        title: "Measures we take",
        items: [
          "Semantic page structure, descriptive headings and labelled form controls.",
          "Keyboard-operable navigation, visible focus treatment and meaningful link and button names.",
          "Alternative text for informative product imagery and text-based product details.",
          "Responsive layouts, readable contrast and support for reduced-motion preferences.",
          "Clear validation messages and status information for key shopping actions.",
        ],
      },
      {
        id: "limitations",
        title: "Known limitations",
        paragraphs: [
          "Product photography cannot convey every physical detail of colour, texture or fit. External payment or delivery services may have accessibility features and limitations outside our direct control. We work to provide equivalent product information and will assist with an alternative route where reasonably possible.",
        ],
      },
      {
        id: "assistance",
        title: "Request assistance",
        paragraphs: [
          `If a page, form or document is difficult to use, email ${email}. Please identify the page, the task you were trying to complete, your device or assistive technology if you are comfortable sharing it, and the format or assistance that would help. We will respond during ${siteConfig.contact.customerCareHours}.`,
        ],
      },
      {
        id: "feedback",
        title: "Feedback and review",
        paragraphs: [
          "We review accessibility feedback when prioritising fixes and test important shopping paths as the site changes. This statement will be updated when material improvements or known limitations change.",
        ],
      },
    ],
    relatedLinks: [
      { label: "Contact", to: "/contact" },
      { label: "Complaints policy", to: "/complaints" },
      { label: "Privacy policy", to: "/privacy" },
      { label: "All policies", to: "/policies" },
    ],
  },
  complaints: {
    title: "Complaints policy",
    intro: "A fair process for raising and resolving concerns about an order or service.",
    sections: [
      {
        id: "contact",
        title: "How to complain",
        paragraphs: [
          `Email ${email} with “Complaint” in the subject line. Include your name, order number if applicable, a concise description of what happened, the outcome you are seeking and any relevant documents or photographs. Please do not send full payment-card details.`,
        ],
      },
      {
        id: "accessibility-support",
        title: "Support and accessibility",
        paragraphs: [
          "Tell us if you need an accessible communication format, additional time, or another person to communicate on your behalf. We may ask for confirmation that the representative is authorised before discussing personal order information.",
        ],
      },
      {
        id: "response",
        title: "Acknowledgement and response",
        paragraphs: [
          "We aim to acknowledge a complaint within two working days and provide a substantive response within ten working days. A complex matter, carrier investigation or technical review may take longer; if so, we will explain the reason and provide an update.",
        ],
      },
      {
        id: "review",
        title: "Review process",
        paragraphs: [
          "We will review the order history, correspondence, product information and relevant provider records, hear the concern impartially and identify any remedy required by the contract, policy or law. Outcomes may include an explanation, correction, apology, replacement, repair, refund or process improvement, depending on the circumstances.",
        ],
      },
      {
        id: "escalation",
        title: "Escalation",
        paragraphs: [
          "If you disagree with the response, reply with the reasons and any new evidence and ask for a second review. Where possible, a person not responsible for the original decision will reconsider the matter. We will tell you when our internal review is complete.",
        ],
      },
      {
        id: "external-rights",
        title: "External rights",
        paragraphs: [
          "This process does not prevent you from contacting a payment issuer, privacy regulator, consumer-protection body, court or other dispute channel available under applicable law. Time limits imposed by those bodies continue to apply while we review a complaint.",
        ],
      },
      {
        id: "records",
        title: "Privacy and records",
        paragraphs: [
          "Complaint information is shared only with people and providers who need it to investigate, respond or comply with law. Records are retained as described in the privacy policy and may be analysed in aggregated form to improve service.",
        ],
      },
    ],
    relatedLinks: [
      { label: "Contact", to: "/contact" },
      { label: "Returns and refunds", to: "/returns" },
      { label: "Accessibility", to: "/accessibility" },
      { label: "All policies", to: "/policies" },
    ],
  },
  security: {
    title: "Security and fraud prevention policy",
    intro: "How we protect orders and how to report suspicious activity.",
    sections: [
      {
        id: "approach",
        title: "Our approach",
        paragraphs: [
          "We use proportionate technical and organisational safeguards for the storefront and customer information, including access controls, secure transmission where supported, payment-provider separation, monitoring and incident procedures. No online service can eliminate every risk.",
        ],
      },
      {
        id: "payment",
        title: "Secure payment handling",
        paragraphs: [
          "Payment-card details are entered on the payment partner's secure page and are not intended to be stored by this storefront. Authentication and fraud controls may be applied by the provider or issuer. We may pause, decline or cancel a transaction that cannot be reasonably verified.",
        ],
      },
      {
        id: "communications",
        title: "Recognising suspicious messages",
        paragraphs: [
          `Our customer-care email is ${email}. We will not ask by email for your full card number, card security code, online-banking password or one-time authentication code. Be cautious of urgent payment requests, lookalike domains, unexpected attachments and messages asking you to move a conversation to an unrelated account.`,
        ],
      },
      {
        id: "customer-steps",
        title: "Steps you can take",
        items: [
          "Check the site address before entering contact or payment information.",
          "Keep your browser and device software current and use a trusted network for checkout.",
          "Do not share authentication codes, banking passwords or full card details by email.",
          "Review order and card notifications and contact the card issuer promptly about an unauthorised charge.",
          "Clear saved bag and wishlist data when using a shared device.",
        ],
      },
      {
        id: "report-fraud",
        title: "Report fraud or impersonation",
        paragraphs: [
          `Forward suspicious messages or report a fake site to ${email}. Include the sender, URL, date and a screenshot where safe. Do not forward live passwords, full card details or malicious files. If payment information may be compromised, also contact the issuer immediately.`,
        ],
      },
      {
        id: "vulnerability",
        title: "Responsible security reporting",
        paragraphs: [
          `Security researchers may report a suspected vulnerability to ${email} with clear reproduction steps and impact. Do not access unnecessary personal data, disrupt service, use social engineering, demand payment as a condition of disclosure or publish the issue before we have had a reasonable opportunity to assess it.`,
        ],
      },
      {
        id: "incidents",
        title: "Security incidents",
        paragraphs: [
          "We assess suspected incidents, contain risk, preserve evidence and notify affected people or authorities when required. We may temporarily restrict a feature while an issue is investigated. Incident-related personal data is handled under the privacy policy.",
        ],
      },
    ],
    relatedLinks: [
      { label: "Privacy policy", to: "/privacy" },
      { label: "Payment policy", to: "/payments" },
      { label: "Acceptable use", to: "/acceptable-use" },
      { label: "All policies", to: "/policies" },
    ],
  },
  "product-safety": {
    title: "Product safety and recalls policy",
    intro: "How to report a safety concern and what happens if corrective action is needed.",
    sections: [
      {
        id: "commitment",
        title: "Safety commitment",
        paragraphs: [
          "We aim to offer products that meet applicable safety requirements and accurately describe relevant materials and use. We review credible safety information from customers, suppliers and authorities and take proportionate corrective action where needed.",
        ],
      },
      {
        id: "safe-use",
        title: "Safe use",
        paragraphs: [
          "Follow the product description, labels and care instructions. Keep packaging and detachable small parts away from young children. Keep garments away from open flames and high heat. Stop using an item if damage creates a hazard or if it causes an unexpected physical reaction.",
        ],
      },
      {
        id: "report",
        title: "Report a safety concern",
        paragraphs: [
          `Email ${email} promptly with the order number, product name, size or batch information shown on labels, a description of the concern, photographs and whether any injury or property damage occurred. If there is an immediate danger, stop using the product and contact the appropriate emergency or safety authority.`,
        ],
      },
      {
        id: "investigation",
        title: "Investigation",
        paragraphs: [
          "We may ask you to preserve the product and packaging, provide additional information or return the item for inspection. Relevant information may be shared with the manufacturer, testing specialists, insurer, delivery provider or authority where necessary to investigate and protect people.",
        ],
      },
      {
        id: "recalls",
        title: "Recalls and corrective action",
        paragraphs: [
          "If a recall or safety notice is required, we may contact affected purchasers using order details, publish clear identification and action instructions, stop sales and work with relevant suppliers and authorities. The remedy may include return, repair, replacement, disposal guidance or refund, depending on the risk and legal requirements.",
        ],
      },
      {
        id: "privacy",
        title: "Contact information for safety notices",
        paragraphs: [
          "Order contact details may be used to deliver an important safety or recall message even if you have opted out of marketing, because a safety notice is a service and legal communication. That information is handled under the privacy policy.",
        ],
      },
    ],
    relatedLinks: [
      { label: "Warranty and faulty goods", to: "/warranty" },
      { label: "Product information and care", to: "/product-information" },
      { label: "Contact", to: "/contact" },
      { label: "All policies", to: "/policies" },
    ],
  },
  "legal-notice": {
    title: "Legal notice",
    intro: `Operator and contact information for the ${brand} online store.`,
    sections: [
      {
        id: "operator",
        title: "Store operator",
        paragraphs: [
          `This online store is operated by ${legalName}, trading as ${brand}. Correspondence may be sent to ${address}. Customer-care and legal enquiries may be sent to ${email}.`,
        ],
      },
      {
        id: "purpose",
        title: "Website purpose",
        paragraphs: [
          "The website provides product information, shopping functions and customer-care information. Product availability, prices, delivery destinations and the final order total are confirmed through the checkout process and order communications.",
        ],
      },
      {
        id: "independence",
        title: "Independent brand",
        paragraphs: [
          `${brand} is presented as an independent store. No affiliation, sponsorship or endorsement by another retailer, designer or brand should be inferred unless a relationship is expressly identified on the relevant page. Third-party names and payment marks belong to their respective owners.`,
        ],
      },
      {
        id: "accuracy",
        title: "Accuracy and availability",
        paragraphs: [
          "We take reasonable care to keep public information accurate, but content may be updated and technical availability cannot be guaranteed at all times. An obvious error does not create a right to purchase at an unintended price; affected customers will be contacted and any captured payment refunded if an order is cancelled.",
        ],
      },
      {
        id: "notices",
        title: "Formal notices",
        paragraphs: [
          `Unless a law requires a different method, a notice about an order may be sent to the email supplied at checkout, and a notice to us may be sent to ${email}. Include the order number and your contact information. Service of court documents or other process must follow the applicable procedural law.`,
        ],
      },
      {
        id: "law",
        title: "Applicable law",
        paragraphs: [
          "The legal terms for the website are governed by the laws of the Hong Kong Special Administrative Region, subject to any mandatory consumer protections that apply in the customer's place of residence. See the terms and conditions for the complete contract provisions.",
        ],
      },
    ],
    relatedLinks: [
      { label: "Terms and conditions", to: "/terms" },
      { label: "Intellectual property", to: "/intellectual-property" },
      { label: "Privacy policy", to: "/privacy" },
      { label: "All policies", to: "/policies" },
    ],
  },
};

const allPolicyLinks = [
  { label: "Terms and conditions", to: "/terms" },
  { label: "Privacy policy", to: "/privacy" },
  { label: "Cookie policy", to: "/cookies" },
  { label: "Shipping policy", to: "/shipping" },
  { label: "Returns and refunds", to: "/returns" },
  ...additionalPolicyRoutes.map(({ label, path }) => ({ label, to: path })),
];

export function CommercePolicy({ policyKey }) {
  const policy = policyDocuments[policyKey];

  return (
    <div className={`policy policy--${policyKey}`}>
      <PolicyPage
        eyebrow="Policies"
        title={policy.title}
        intro={policy.intro}
        sections={policy.sections}
        relatedLinks={policy.relatedLinks}
      />
    </div>
  );
}

export function PolicyCentre() {
  const sections = [
    {
      id: "shopping",
      title: "Shopping and orders",
      paragraphs: [
        "Information about prices, payment, delivery, cancellations, returns, product care, faults, promotions and safety. Read the policy relevant to your order together with the terms and conditions.",
      ],
    },
    {
      id: "privacy-rights",
      title: "Privacy and website rights",
      paragraphs: [
        "Information about personal data, browser storage, security, accessibility, acceptable use, intellectual property and complaints. These documents are written for this store and do not copy another retailer's policy text.",
      ],
    },
    {
      id: "contact",
      title: "Need help?",
      paragraphs: [
        `Email ${email} and include your order number where relevant. Customer care is available ${siteConfig.contact.customerCareHours}. Policies do not limit rights that cannot lawfully be excluded.`,
      ],
    },
  ];

  return (
    <div className="policy policy--centre">
      <PolicyPage
        eyebrow="Customer care"
        title="Policy centre"
        intro={`All store, customer-care and legal policies for ${brand} in one place.`}
        sections={sections}
        relatedLinks={allPolicyLinks}
      />
    </div>
  );
}
